// src/components/LoanFilesTable/hooks/useColumnDefs.js
import { useMemo } from "react";

import isNil from "lodash/isNil";
import { columnDefSuppressFloatingFilterButton } from "../../../../_src_shared/agGrid/columnDef/columnDefSuppressFloatingFilterButton";
import CellRendererFileActions from "../../../../components/agGrid/cellRenderer/CellRendererFileActions";
import OpenInNewTabButton from "../../../../components/ui/buttons/OpenInNewTabButton";

/**
 * Builds the array of column definitions. Depends on `groupByCategory` and `editing`.
 */
export const useColumnDefs = (groupByCategory, editing) => {
  return useMemo(
    () => [
      {
        width: 120,
        maxWidth: 120,

        flex: 0,
        headerName: "Actions",
        resizable: false,
        hide: editing,
        cellStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          paddingLeft: "7px",
        },
        cellRenderer: (params) => {
          if (params.node.group) {
            return params.value;
          }
          return <CellRendererFileActions params={params} />;
        },
      },
      {
        headerName: "Loan Number",
        field: "loanNumber",
        minWidth: 115,
        maxWidth: 115,
        width: 115,
        suppressAutoSize: true,
        suppressHeaderFilterButton: true,
        suppressMenu: true,
        // ...columnDefSuppressFloatingFilterButton,
        flex: 0,
        filter: "agTextColumnFilter",
        cellRenderer: (params) => {
          const loanNumber = params?.data?.loanNumber;
          if (isNil(loanNumber)) {
            return "N/A";
          }
          return (
            <OpenInNewTabButton linkIcon to={`/loan/${loanNumber}`}>
              {loanNumber}
            </OpenInNewTabButton>
          );
        },
        ...columnDefSuppressFloatingFilterButton,
      },
      // {
      //   field: "loanNumber",
      //   headerName: "Loan Number",
      //   sortable: true,
      // },
      {
        field: "loanName",
        headerName: "Loan Name",
        sortable: true,
        valueFormatter: (params) => {
          const loanName = params?.data?.loanName;
          if (isNil(loanName)) {
            return "N/A";
          }
          return loanName;
        },
      },
      {
        field: "file_display_name",
        headerName: "File Display Name",
        width: 280,
      },
      { field: "_id", headerName: "ID", hide: true },
      { field: "dealId", headerName: "Deal ID", hide: true },
      {
        field: "sizer_stage_at_upload",
        headerName: "Sizer Stage At Upload",
        // hide: true,
      },
      {
        field: "sizer_type_at_upload",
        headerName: "Sizer Type At Upload",
        // hide: true,
      },
      { field: "containerName", headerName: "Container Name", hide: true },
      {
        field: "file_name_as_loaded",
        headerName: "File Name As Loaded",
        hide: true,
      },

      {
        field: "file_storage_name",
        headerName: "File Storage Name",
        hide: true,
      },
      { field: "content_type", headerName: "Content Type", hide: true },
      { field: "size", headerName: "Size", hide: true },
      { field: "uploadedBy", headerName: "Uploaded By" },
      {
        field: "uploadDate",
        headerName: "Upload Date",
        type: "date",
        sort: "desc",
        filter: false,
      },
      { field: "upload_loanStatus", headerName: "Upload Loan Status" },
      {
        field: "upload_expectedClosingDate",
        headerName: "Upload Expected Closing Date",
        type: "date",
        filter: false,
      },
      { field: "upload_loanPurpose", headerName: "Upload Loan Purpose" },
      { field: "upload_interestRate", headerName: "Upload Interest Rate" },
      { field: "upload_discountPoints", headerName: "Upload Discount Points" },
      {
        field: "upload_originationPoints",
        headerName: "Upload Origination Points",
      },
      {
        field: "upload_baseLoanAmount",
        headerName: "Upload Base Loan Amount",
        type: "dollar",
        filter: false,
      },
      {
        field: "upload_totalLoanAmount",
        headerName: "Upload Total Loan Amount",
        type: "dollar",
        filter: false,
      },
      { field: "extract_requested", headerName: "Extract Requested" },
      {
        field: "upload_loanProductType",
        headerName: "Upload Loan Product Type",
      },
      // loan

      {
        field: "createDateTime",
        headerName: "Created At",
        sortable: true,
      },
      {
        field: "updateDateTime",
        headerName: "Updated At",
        sortable: true,
      },
      {
        field: "lastViewDateTime",
        headerName: "Last View",
        sortable: true,
      },

      {
        field: "loanPurpose",
        headerName: "Purpose",
        sortable: true,
      },
      {
        field: "loanProductType",
        headerName: "Product Type",
        sortable: true,
      },
      {
        field: "loanStatus",
        headerName: "Status",
        sortable: true,
      },
      {
        field: "expectedClosingDate",
        headerName: "Expected Closing",
        sortable: true,
      },
      // {
      //   field: "comments",
      //   headerName: "Comments",
      //   sortable: false,
      //   wrapText: true,
      //   autoHeight: true,
      // },
      {
        field: "salesperson",
        headerName: "Salesperson",
        sortable: true,
      },

      // Always visible numeric fields
      {
        field: "interestRate",
        headerName: "Interest Rate",
        sortable: true,
        valueFormatter: (params) =>
          params.value != null ? `${params.value}%` : "",
      },
      {
        field: "discountPoints",
        headerName: "Discount Points",
        sortable: true,
      },
      {
        field: "originationPoints",
        headerName: "Origination Points",
        sortable: true,
      },

      // Custom concatenations
      {
        headerName: "Borrowers",
        field: "borrowers",
        valueGetter: (params) => {
          const list = params.data.borrowers || [];
          return list.map((b) => `${b.firstName} ${b.lastName}`).join(", ");
        },
      },
      {
        headerName: "Subject Properties",
        field: "subjectProperties",
        valueGetter: (params) => {
          const props = params.data.subjectProperties || [];
          return props
            .map((p) => `${p.street}, ${p.city}, ${p.state} ${p.postalCode}`)
            .join("; ");
        },
      },

      // Hidden by default (but available via tool panel or API for add/removal)
      { field: "totalHoldback", headerName: "Total Holdback", hide: true },
      { field: "baseLoanAmount", headerName: "Base Loan Amount", hide: true },
      { field: "totalLoanAmount", headerName: "Total Loan Amount", hide: true },
      {
        field: "existingLoanInDefault",
        headerName: "Existing Loan In Default",
        hide: true,
      },
      {
        field: "baseLoanToAsIsValue",
        headerName: "Loan to As-Is Value",
        hide: true,
      },
      {
        field: "baseLoanToPurchasePrice",
        headerName: "Loan to Purchase Price",
        hide: true,
      },
      { field: "totalLoanToArv", headerName: "Loan to ARV", hide: true },
      {
        field: "guidelineConforming",
        headerName: "Guideline Conforming",
        hide: true,
      },
      { field: "totalArv", headerName: "Total ARV", hide: true },
      { field: "totalAsIsValue", headerName: "Total As-Is Value", hide: true },
      {
        field: "totalConstructionBudget",
        headerName: "Construction Budget",
        hide: true,
      },
      { field: "totalCostBasis", headerName: "Total Cost Basis", hide: true },
      { field: "totalPurchasePrice", headerName: "Purchase Price", hide: true },
    ],
    [editing]
  );
};
